import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Loader2, RefreshCw, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { getOfferings, purchasePackage, restorePurchases as rcRestore, initRevenueCat } from '../lib/revenuecat';

const AppleIAPButton = ({ onPurchaseSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [offerings, setOfferings] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    loadOfferings();
  }, []);

  const loadOfferings = async () => {
    setLoading(true);
    try {
      await initRevenueCat();
      const pkgs = await getOfferings();
      setOfferings(pkgs);
    } catch (e) {
      console.error('[IAP] loadOfferings error:', e);
    }
    setLoading(false);
  };

  const handlePurchase = async (pkg) => {
    setPurchasing(true);
    try {
      const result = await purchasePackage(pkg);
      if (result.success) {
        toast.success('Achat réussi ! Bienvenue dans le Club.');
        if (onPurchaseSuccess) onPurchaseSuccess();
      }
    } catch (e) {
      if (e.userCancelled) {
        toast.info('Achat annulé');
      } else {
        toast.error('Erreur lors de l\'achat');
      }
    }
    setPurchasing(false);
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const result = await rcRestore();
      if (result.success) {
        toast.success('Achats restaurés');
        if (onPurchaseSuccess) onPurchaseSuccess();
      } else {
        toast.info('Aucun achat trouvé');
      }
    } catch (e) {
      toast.error('Erreur de restauration');
    }
    setRestoring(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4" data-testid="iap-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#0D47A1] mr-2" />
        <span className="text-sm text-slate-500">Chargement des offres...</span>
      </div>
    );
  }

  if (offerings.length === 0) {
    return (
      <div className="text-center py-4" data-testid="iap-no-offerings">
        <p className="text-sm text-slate-500 mb-3">Les offres ne sont pas disponibles actuellement</p>
        <div className="flex flex-col gap-2 items-center">
          <Button
            variant="default"
            size="sm"
            className="bg-[#0D47A1] hover:bg-[#0D47A1]/90 text-white"
            onClick={loadOfferings}
            data-testid="iap-retry-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser les offres
          </Button>
          <Button variant="outline" size="sm" onClick={handleRestore} disabled={restoring} data-testid="iap-restore-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${restoring ? 'animate-spin' : ''}`} />
            Restaurer mes achats
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full px-2" data-testid="iap-products">
      {offerings.map((pkg, i) => (
        <Button
          key={pkg.identifier || i}
          onClick={() => handlePurchase(pkg)}
          disabled={purchasing}
          className="w-full bg-gradient-to-r from-[#0D47A1] to-[#1565C0] hover:from-[#0A367A] hover:to-[#0D47A1] text-white py-5 rounded-xl text-left overflow-hidden"
          data-testid={`iap-buy-${pkg.identifier || i}`}
        >
          <div className="flex items-center gap-2 w-full min-w-0">
            {purchasing ? (
              <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
            ) : (
              <Crown className="h-5 w-5 flex-shrink-0" />
            )}
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-sm truncate">{pkg.product?.title || 'Club Mensuel'}</span>
              <span className="text-xs opacity-80">{pkg.product?.priceString || ''}/mois</span>
            </div>
          </div>
        </Button>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={handleRestore}
        disabled={restoring}
        className="w-full"
        data-testid="iap-restore-btn"
      >
        <RefreshCw className={`h-4 w-4 mr-2 ${restoring ? 'animate-spin' : ''}`} />
        Restaurer mes achats
      </Button>
      <div className="text-center text-xs text-slate-400 mt-2 space-y-1" data-testid="iap-legal-links">
        <p>Abonnement auto-renouvelable. Annulable à tout moment.</p>
        <div className="flex justify-center gap-3">
          <a href="https://www.10minvestor.com/cgu" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Conditions d'utilisation</a>
          <span>|</span>
          <a href="https://www.10minvestor.com/confidentialite" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  );
};

export default AppleIAPButton;
